import { useState, useEffect } from "react";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import DomainCard from "@/components/DomainCard";
import { getDomains, getCategories, getSettings } from "@/lib/apiClient";

const PAGE_SIZE = 9;

export default function Catalog({ onInquire, onBuy, reloadSignal }) {
  const [domains, setDomains] = useState([]);
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [glitter, setGlitter] = useState("sparkle");
  const [page, setPage] = useState(1);

  useEffect(() => {
    getCategories().then((c) => setCats(["All", ...c])).catch(() => {});
    getSettings().then((s) => setGlitter(s.glitter_intensity || "sparkle")).catch(() => {});
  }, [reloadSignal]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      getDomains({ search: search || undefined, category: activeCat })
        .then((d) => { setDomains(d); setPage(1); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [search, activeCat, reloadSignal]);

  const totalPages = Math.max(1, Math.ceil(domains.length / PAGE_SIZE));
  const pageItems = domains.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const goTo = (p) => {
    setPage(p);
    document.getElementById("collection")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="collection" data-testid="catalog" className="relative pt-12 pb-28 md:pt-14 md:pb-40 px-6 md:px-12">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[70vw] h-[34vh] bg-violet/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="relative max-w-[1600px] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-center justify-end gap-6 mb-10">
          <div className="relative w-full lg:w-96">
            <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-ink-dim" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="catalog-search"
              placeholder="Search domains…"
              className="w-full bg-transparent border-b border-white/15 focus:border-gold text-ink font-mono text-sm pl-7 pr-2 py-3 focus:outline-none transition-colors placeholder:text-[#52525b]"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-12" data-testid="catalog-filters">
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setActiveCat(c)}
              data-testid={`filter-${c}`}
              className={`font-mono text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-full border transition-colors duration-300 ${
                activeCat === c
                  ? "bg-gold text-bg border-gold"
                  : "text-ink-dim border-white/10 hover:border-gold/50 hover:text-ink"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="min-h-[260px] rounded-2xl border border-white/5 bg-white/[0.02] animate-pulse" />
            ))}
          </div>
        ) : domains.length === 0 ? (
          <div className="py-24 text-center font-mono text-sm text-ink-dim" data-testid="catalog-empty">
            No domains match your search.
          </div>
        ) : (
          <>
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch glitter-${glitter}`}>
              {pageItems.map((d, i) => (
                <DomainCard key={d.id} domain={d} index={i} onInquire={onInquire} onBuy={onBuy} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-14 flex items-center justify-center gap-2" data-testid="catalog-pagination">
                <button
                  onClick={() => goTo(page - 1)}
                  disabled={page === 1}
                  data-testid="pagination-prev"
                  className="flex items-center justify-center w-10 h-10 rounded-full border border-white/10 text-ink-dim hover:border-gold/50 hover:text-ink transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-white/10 disabled:hover:text-ink-dim"
                  aria-label="Previous page"
                >
                  <ChevronLeft size={16} />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goTo(i + 1)}
                    data-testid={`pagination-page-${i + 1}`}
                    className={`w-10 h-10 rounded-full font-mono text-xs transition-colors ${
                      page === i + 1
                        ? "bg-gold text-bg"
                        : "border border-white/10 text-ink-dim hover:border-gold/50 hover:text-ink"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => goTo(page + 1)}
                  disabled={page === totalPages}
                  data-testid="pagination-next"
                  className="flex items-center justify-center w-10 h-10 rounded-full border border-white/10 text-ink-dim hover:border-gold/50 hover:text-ink transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-white/10 disabled:hover:text-ink-dim"
                  aria-label="Next page"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
